import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { fetchWithAuth } from '../../../api/backendApi';

/**
 * Raw API Request Tool
 * Allows manual testing of any backend endpoint with automatic Firebase auth
 */
const RawApiRequest = () => {
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const handleSendRequest = async () => {
    // Validate URL
    if (!url || !url.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      // Parse body if provided
      let parsedBody = null;
      if (body && body.trim() && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
        try {
          parsedBody = JSON.parse(body);
        } catch (e) {
          toast.error('Invalid JSON in request body');
          setLoading(false);
          return;
        }
      }

      const startTime = performance.now();
      
      // Make the request using fetchWithAuth
      const result = await fetchWithAuth(url.trim(), method, parsedBody);
      
      const duration = Math.floor(performance.now() - startTime);

      // Set response with metadata
      setResponse({
        success: true,
        status: 200,
        duration,
        data: result,
      });

      toast.success(`Request completed in ${duration}ms`);
    } catch (error) {
      console.error('API Request Error:', error);
      
      const errorResponse = {
        success: false,
        status: error.status || error.statusCode || 500,
        duration: 0,
        error: error.message || 'Request failed',
        details: error.details || error.data || null,
      };

      setResponse(errorResponse);
      toast.error(`Request failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setUrl('');
    setBody('');
    setResponse(null);
  };

  const quickFillExample = (exampleUrl, exampleMethod, exampleBody = '') => {
    setUrl(exampleUrl);
    setMethod(exampleMethod);
    setBody(exampleBody);
    setResponse(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Raw API Request</h2>
        <p className="text-sm text-gray-600">
          Manually call any backend endpoint with automatic Firebase authentication
        </p>
      </div>

      {/* Quick Examples */}
      <div className="mb-6">
        <p className="text-sm font-medium text-gray-700 mb-2">Quick Examples:</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => quickFillExample('/api/v2/device-intelligence/rebuild', 'POST')}
            className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1 rounded border border-blue-200"
          >
            Rebuild Intelligence
          </button>
          <button
            onClick={() => quickFillExample('/api/v2/device-config/schema/unified/rebuild', 'POST')}
            className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1 rounded border border-blue-200"
          >
            Rebuild Unified Schemas
          </button>
          <button
            onClick={() => quickFillExample('/api/v2/admin/tenants', 'GET')}
            className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1 rounded border border-blue-200"
          >
            Get Tenants
          </button>
        </div>
      </div>

      {/* Method Selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">HTTP Method</label>
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="DELETE">DELETE</option>
          <option value="PATCH">PATCH</option>
        </select>
      </div>

      {/* URL Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          URL (relative path)
        </label>
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="/api/v2/device-intelligence/rebuild"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          Enter a relative path (e.g., /api/v2/...). Auth headers are added automatically.
        </p>
      </div>

      {/* Body Input (for POST/PUT/PATCH) */}
      {(method === 'POST' || method === 'PUT' || method === 'PATCH') && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Request Body (JSON)
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder='{\n  "key": "value"\n}'
            rows="6"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            Optional. Leave empty to send no body. Must be valid JSON.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={handleSendRequest}
          disabled={loading}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-md font-medium flex items-center justify-center"
        >
          {loading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Send Request
            </>
          )}
        </button>
        <button
          onClick={handleClear}
          disabled={loading}
          className="bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-700 px-4 py-3 rounded-md font-medium"
        >
          Clear
        </button>
      </div>

      {/* Response Display */}
      {response && (
        <div className="border-t pt-6">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold">Response</h3>
            <div className="flex items-center gap-3">
              {response.duration > 0 && (
                <span className="text-sm text-gray-600">
                  {response.duration}ms
                </span>
              )}
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  response.success
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {response.success ? `✓ ${response.status || 200}` : `✗ ${response.status || 500}`}
              </span>
            </div>
          </div>

          {/* Response Body */}
          <div className="bg-gray-50 rounded-md p-4 border border-gray-200 max-h-96 overflow-auto">
            <pre className="text-xs font-mono whitespace-pre-wrap break-words">
              {response.success
                ? JSON.stringify(response.data, null, 2)
                : JSON.stringify(
                    {
                      error: response.error,
                      status: response.status,
                      details: response.details,
                    },
                    null,
                    2
                  )}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default RawApiRequest;
